import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { toast } from "sonner";
import { useLearner } from "../../contexts/LearnerContext";
import { languages } from "../../data/languages";
import { PageHeader } from "../../components/app/PageHeader";
import { LanguageGrid } from "../../components/courses/LanguageGrid";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { PairChips } from "../../components/ui/LangChip";
import { Tag } from "../../components/ui/Tag";
import { basesFor, courseTitle, getLanguage, isBaseLanguage, targetsFrom } from "../../utils/language";
import { learnPath } from "../../utils/routes";
import { spring } from "../../utils/motion";
export function CoursePicker() {
  const {
    courses,
    addCourse
  } = useLearner();
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;
  const [step, setStep] = useState<1 | 2>(1);
  const [base, setBase] = useState<string>(courses[0]?.base ?? "en");
  const [target, setTarget] = useState<string | undefined>();
  const baseCodes = useMemo(() => [...languages].sort((a, b) => Number(isBaseLanguage(b.code)) - Number(isBaseLanguage(a.code))).map(l => l.code), []);
  const knownBases = useMemo(() => new Set([base, ...courses.map(c => c.base)]), [base, courses]);
  const owned = (t: string) => courses.some(c => c.id === `${t}-${base}`);
  const confirm = () => {
    if (!target) return;
    const already = owned(target);
    addCourse(target, base);
    toast.success(already ? "Jumping back into your course" : `${courseTitle({
      target,
      base
    })} added`, {
      description: "Your dashboard now tracks this pair separately."
    });
    navigate(learnPath({
      target,
      base
    }));
  };
  const slide = reduce ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      x: 24
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -24
    }
  };
  return <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10" data-id="element-1285" data-mp="2deeg0ekzvw68">
      <AnimatePresence mode="wait" initial={false} data-id="element-1286" data-mp="1yp8kb0ld8o33">
        {step === 1 ? <motion.div key="s1" {...slide} transition={spring.smooth} data-id="element-1287" data-mp="1yn5wmn7or71j">
            <PageHeader eyebrow={<Tag tone="tint" data-id="element-1289" data-mp="23m8thkdii6yf">Step 1 of 2</Tag>} title="I speak…" description="Choose the language you'd like to be taught in. The same target language can be learned from several of these." data-id="element-1288" data-mp="23n0mqojmzslj" />
            <div className="mt-6" data-id="element-1290" data-mp="1fqw9zbipx85x">
              <LanguageGrid codes={baseCodes} selected={base} searchLabel="Search languages" disabledReason={c => isBaseLanguage(c) ? undefined : "Courses from this language coming soon"} meta={c => <Tag tone="neutral" data-id="element-1292" data-mp="3f40ad6v1hj98">{targetsFrom(c).length} courses</Tag>} onSelect={c => {
            setBase(c);
            setTarget(undefined);
            setStep(2);
          }} data-id="element-1291" data-mp="1fro38fouett1" />
            </div>
          </motion.div> : <motion.div key="s2" {...slide} transition={spring.smooth} data-id="element-1293" data-mp="1d6gh7jkspgl9">
            <button type="button" onClick={() => setStep(1)} className="focus-ring mb-4 flex items-center gap-1.5 rounded-lg text-sm font-semibold text-ink-2 hover:text-ink" data-id="element-1294" data-mp="1d7rf52rk7u4g">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-1295" data-mp="1i5t0jucgk5j3" /> Taught in {getLanguage(base).name} · change
            </button>
            <PageHeader eyebrow={<Tag tone="tint" data-id="element-1297" data-mp="1w2q14nidp1d6">Step 2 of 2</Tag>} title="I want to learn…" description={`${targetsFrom(base).length} languages are available from ${getLanguage(base).name}.`} data-id="element-1296" data-mp="1w3re4xiyfxxj" />
            <div className="mt-6 pb-28" data-id="element-1298" data-mp="1smc1xnintkb">
              <LanguageGrid codes={targetsFrom(base)} selected={target} searchLabel="Search courses" onSelect={setTarget} meta={c => {
            const others = basesFor(c).filter(b => b !== base && knownBases.has(b));
            return <>
                      {owned(c) && <Tag tone="good" data-id="element-1300" data-mp="d6o2bq4k0qb8">In progress</Tag>}
                      <Tag data-id="element-1301" data-mp="d5cwlkrjt8hh">{getLanguage(c).units} units</Tag>
                      {getLanguage(c).loescher && <Tag tone="tint" data-id="element-1302" data-mp="3bpbe3txcmc9l">Loescher</Tag>}
                      {others.map(o => <Tag key={o} data-id="element-1303" data-mp="3gnd1us5pyzre">Also from {o.toUpperCase()}</Tag>)}
                    </>;
          }} data-id="element-1299" data-mp="3sy4a93wouoys" />
            </div>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-1304" data-mp="2sxadam10kbxj">
        {step === 2 && target && <motion.div initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} transition={spring.stable} className="fixed inset-x-4 bottom-24 z-40 lg:bottom-6 lg:left-[calc(16rem+2rem)] lg:right-8" data-id="element-1305" data-mp="2sx0j0m8xrim1">
            <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-card border-3 border-line bg-surface p-4 shadow-brutal-lg sm:flex-row sm:items-center" data-id="element-1306" data-mp="15zrloxkkaoqi">
              <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-1307" data-mp="113u1uha3b08z">
                <PairChips target={target} base={base} data-id="element-1308" data-mp="w3y24u8fduz8" />
                <div className="min-w-0 pl-1" data-id="element-1309" data-mp="rb4u9xhnu6ix">
                  <p className="truncate text-[15px] font-bold text-ink" data-id="element-1310" data-mp="radb5m59219j">{courseTitle({
                  target,
                  base
                })}</p>
                  <p className="text-sm text-ink-2" data-id="element-1311" data-mp="n7ysp35ht3is">Starts at A1 · placement test optional</p>
                </div>
              </div>
              <BrutalButton onClick={confirm} data-id="element-1312" data-mp="dcexkiyr68v3">
                {owned(target) ? "Continue course" : "Start course"} <ArrowRightIcon className="h-4 w-4" data-id="element-1313" data-mp="8cafwpm3z83a" />
              </BrutalButton>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}